import React from "react";
import { Chip, Typography, ToggleButton, ToggleButtonGroup, Snackbar, Alert } from "@mui/material";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import LocationRepository from "../../Repositories/LocationRepository";
import {
    SELECTABLE_CURRENCIES,
    DEFAULT_CURRENCIES,
    DEFAULT_EXACT_CALCULATION
} from "../../Types/CurrencyDefaults";

interface CurrencyRoundingSettingProps {
    // Called after the settings are loaded and whenever they change, so a host
    // page can (re)configure its calculation service with the same values.
    onChange?: (currencies: number[], exactMode: boolean) => void;
    // Compact mode drops the descriptive headings for embedding inside the
    // count pages; the settings page uses the full layout.
    compact?: boolean;
    accent?: string;
}

interface CurrencyRoundingSettingState {
    selectedCurrencies: number[];
    exactMode: boolean;
    loaded: boolean;
    errorMessage: string;
    showError: boolean;
}

export default class CurrencyRoundingSetting extends React.Component<CurrencyRoundingSettingProps, CurrencyRoundingSettingState> {

    static contextType = AuthContext;
    context!: AuthContextType;

    constructor(props: CurrencyRoundingSettingProps) {
        super(props);

        this.toggleCurrency = this.toggleCurrency.bind(this);
        this.setMode = this.setMode.bind(this);
        this.persist = this.persist.bind(this);

        this.state = {
            selectedCurrencies: [...DEFAULT_CURRENCIES],
            exactMode: DEFAULT_EXACT_CALCULATION,
            loaded: false,
            errorMessage: "",
            showError: false
        };
    }

    public async componentDidMount(): Promise<void> {
        const result = await LocationRepository.getLocationSettings(this.context.locationId);
        if (result.isFailed() || !result.result) {
            this.setState({ loaded: true });
            this.props.onChange?.(this.state.selectedCurrencies, this.state.exactMode);
            return;
        }

        this.setState({
            selectedCurrencies: result.result.currencies,
            exactMode: result.result.exactCalculation,
            loaded: true
        });

        this.props.onChange?.(result.result.currencies, result.result.exactCalculation);
    }

    private async persist(currencies: number[], exactMode: boolean): Promise<void> {
        this.props.onChange?.(currencies, exactMode);

        const result = await LocationRepository.updateLocationSettings(this.context.locationId, {
            currencies,
            exactCalculation: exactMode
        });

        if (result.isFailed()) {
            this.setState({
                showError: true,
                errorMessage: result.errorMessage ?? "Could not save the payout settings. Please try again."
            });
        }
    }

    private toggleCurrency(value: number): void {
        const exists = this.state.selectedCurrencies.includes(value);
        const next = exists
            ? this.state.selectedCurrencies.filter(v => v !== value)
            : [...this.state.selectedCurrencies, value].sort((a, b) => b - a);

        // Never allow an empty currency set: without any denomination there is
        // nothing to pay out with and the whole amount would become debt.
        if (next.length === 0) return;

        this.setState({ selectedCurrencies: next }, () => this.persist(next, this.state.exactMode));
    }

    private setMode(exactMode: boolean): void {
        if (exactMode === this.state.exactMode) return;
        this.setState({ exactMode }, () => this.persist(this.state.selectedCurrencies, exactMode));
    }

    public render() {
        const accent = this.props.accent ?? "#c79656";
        const compact = this.props.compact ?? false;
        const selected = new Set(this.state.selectedCurrencies);

        const formatCurrency = (v: number) => (v >= 1 ? `€${v}` : `${Math.round(v * 100)}c`);

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: compact ? 10 : 16 }}>
                {!compact && (
                    <div>
                        <Typography variant="h6" sx={{ fontSize: "18px" }}>Payout denominations & rounding</Typography>
                        <Typography variant="body2" sx={{ fontSize: "12px" }}>
                            Which coins and notes are available for payout, and whether payouts are made exact or rounded (the difference is carried as debt).
                        </Typography>
                    </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Rounding mode
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={this.state.exactMode ? "exact" : "rounded"}
                        onChange={(_e, val) => { if (val) this.setMode(val === "exact"); }}
                        sx={{
                            "& .MuiToggleButton-root.Mui-selected": {
                                background: `${accent}22`,
                                color: "#1a1a1a",
                                borderColor: `${accent}55`
                            }
                        }}
                    >
                        <ToggleButton value="exact" sx={{ textTransform: "none", fontWeight: 700 }}>Exact</ToggleButton>
                        <ToggleButton value="rounded" sx={{ textTransform: "none", fontWeight: 700 }}>Rounded</ToggleButton>
                    </ToggleButtonGroup>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Available denominations
                    </Typography>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {SELECTABLE_CURRENCIES.map(v => {
                            const active = selected.has(v);
                            return (
                                <Chip
                                    key={v}
                                    label={formatCurrency(v)}
                                    onClick={() => this.toggleCurrency(v)}
                                    variant={active ? "filled" : "outlined"}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        background: active ? `${accent}22` : "transparent",
                                        borderColor: active ? `${accent}66` : "rgba(0,0,0,0.2)",
                                        color: active ? "#1a1a1a" : "rgba(0,0,0,0.55)"
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>

                <Snackbar
                    open={this.state.showError}
                    autoHideDuration={4000}
                    onClose={() => this.setState({ showError: false })}
                >
                    <Alert severity="error" onClose={() => this.setState({ showError: false })}>
                        {this.state.errorMessage}
                    </Alert>
                </Snackbar>
            </div>
        );
    }
}
